import { PROJECTION, SCREENSAVERS_API_URL, SCREENSAVERS_FILTER_URL, SCREENSAVERS_SEARCH_AVAILABLE_URL } from 'constants/ApiConstants';
import * as types from 'modules/screensaver/actionTypes';

/**
 * @param {import("../types/actions").GetScreensaversPayload} payload
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").GetScreensaversAction}
 */
export const fetchScreenSavers = (payload, meta = {}) => ({
  type: types.FETCH_SCREENSAVERS,
  payload: {
    request: {
      method: 'GET',
      url: SCREENSAVERS_FILTER_URL,
      params: {
        projection: PROJECTION.PAGE,
        ...payload
      }
    }
  },
  meta: {
    ...meta,
    '@app-request': true
  }
});

/**
 * @param {import("../types/actions").GetScreensaversPayload} payload
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").FetchScreenSaversSelectAction}
 */
export const fetchScreenSaversSelect = (payload, meta = {}) => ({
  type: types.FETCH_AVAILABLE_SCREENSAVERS,
  payload: {
    request: {
      method: 'GET',
      url: SCREENSAVERS_SEARCH_AVAILABLE_URL,
      params: {
        projection: PROJECTION.PAGE,
        ...payload
      }
    }
  },
  meta: {
    ...meta,
    '@app-request': true,
    source: 'screensaver-select'
  }
});

/**
 * @param {import("modules/global/generic.types").PageContent<import("../types/screensaver-data").Screensaver>} payload
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").FetchScreenSaversSelectSuccessAction}
 */
export const fetchScreenSaversSelectSuccess = (payload, meta = {}) => ({
  type: types.FETCH_AVAILABLE_SCREENSAVERS_SUCCESS,
  payload,
  meta
});

/**
 * @param {string} screensaverId
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").GetScreensaverAction}
 */
export const fetchScreensaver = (screensaverId, meta = {}) => ({
  type: types.FETCH_SCREENSAVER,
  payload: {
    request: {
      method: 'GET',
      url: `${SCREENSAVERS_API_URL}/${screensaverId}`,
      params: {
        projection: PROJECTION.DETAILS
      }
    }
  },
  meta: {
    ...meta,
    '@app-request': true
  }
});

/**
 * @param {Partial<import("../types/screensaver-data").Screensaver>} screensaver
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").CreateScreensaverAction}
 */
export const createScreensaver = (screensaver, meta = {}) => ({
  type: types.CREATE_SCREENSAVER,
  payload: {
    request: {
      method: 'POST',
      url: SCREENSAVERS_API_URL,
      data: screensaver
    }
  },
  meta
});

/**
 * @param {import("../types/screensaver-data").Screensaver} screensaver
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").CreateScreensaverSuccessAction}
 */
export const createScreensaverSuccess = (screensaver, meta = {}) => ({
  type: types.CREATE_SCREENSAVER_SUCCESS,
  payload: screensaver,
  meta
});

/**
 * @param {string} screensaverId
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").DeleteScreensaverAction}
 */
export const deleteScreensaver = (screensaverId, meta = {}) => ({
  type: types.DELETE_SCREENSAVER,
  payload: {
    screensaverId,
    request: {
      method: 'DELETE',
      url: `${SCREENSAVERS_API_URL}/${screensaverId}`
    }
  },
  meta
});

/**
 * @param {string} screensaverId
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").DeleteScreensaverSuccessAction}
 */
export const deleteScreensaverSuccess = (screensaverId, meta = {}) => ({
  type: types.DELETE_SCREENSAVER_SUCCESS,
  payload: screensaverId,
  meta
});

/**
 * @param {Partial<import("../types/screensaver-data").Screensaver>} screensaver
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").UpdateScreensaverAction}
 */
export const updateScreensaver = (screensaver, meta = {}) => {
  const { id: screensaverId } = screensaver;

  return {
    type: types.UPDATE_SCREENSAVER,
    payload: {
      request: {
        method: 'PUT',
        url: `${SCREENSAVERS_API_URL}/${screensaverId}`,
        data: screensaver
      }
    },
    meta
  };
};

/**
 * @param {import("../types/screensaver-data").Screensaver} screensaver
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").UpdateScreensaverSuccessAction}
 */
export const updateScreensaverSuccess = (screensaver, meta = {}) => ({
  type: types.UPDATE_SCREENSAVER_SUCCESS,
  payload: screensaver,
  meta
});

/**
 * @param {string} screensaverId
 * @param {{ groupId: string, dealerId?: string }} data
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").CloneScreensaverAction}
 */
export const cloneScreensaver = (screensaverId, data, meta = {}) => ({
  type: types.CLONE_SCREENSAVER,
  payload: {
    request: {
      method: 'POST',
      url: `${SCREENSAVERS_API_URL}/${screensaverId}/clone`,
      data
    }
  },
  meta
});

/**
 * @param {import("../types/screensaver-data").Screensaver} screensaver
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").CloneScreensaverSuccessAction}
 */
export const cloneScreensaverSuccess = (screensaver, meta = {}) => ({
  type: types.CLONE_SCREENSAVER_SUCCESS,
  payload: screensaver,
  meta
});

/**
 * @param {string} screensaverId
 * @param {Object} [meta={}]
 * @returns {import("../types/actions").PreviewTokenScreensaverAction}
 */
export const previewTokenScreensaver = (screensaverId, meta = {}) => ({
  type: types.PREVIEW_TOKEN_SCREENSAVER,
  payload: {
    screensaverId,
    request: {
      method: 'GET',
      url: `${SCREENSAVERS_API_URL}/${screensaverId}/token`
    }
  },
  meta
});
